/**
 * Who has a profile picture, and which version of it, so every Avatar can show
 * it without each list of people having to carry it. Filled from /api/users the
 * first time an avatar is drawn, and from the signed-in account whenever that
 * changes, so your own new picture shows everywhere straight away.
 */
import { useSyncExternalStore } from "react";
import { api } from "./api";

const versions = new Map<number, number>();
const listeners = new Set<() => void>();
let loading: Promise<void> | null = null;

function changed() {
  for (const fn of listeners) fn();
}

/** Record someone's picture version, or null when they have none. */
export function noteAvatar(id: number, version: number | null | undefined) {
  if ((versions.get(id) ?? null) === (version || null)) return;
  if (version) versions.set(id, version);
  else versions.delete(id);
  changed();
}

function load() {
  loading ??= api
    .get<{ users: Array<{ id: number; avatar: number | null }> }>("/api/users")
    .then((r) => {
      for (const u of r.users) {
        if (u.avatar) versions.set(u.id, u.avatar);
        else versions.delete(u.id);
      }
      changed();
    })
    .catch(() => {
      loading = null; // try again when the next avatar appears
    });
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  load();
  return () => listeners.delete(fn);
}

/** The URL of someone's profile picture, or null when they have none. Ids that aren't accounts (strings) never do. */
export function useAvatarUrl(id: number | string): string | null {
  const version = useSyncExternalStore(subscribe, () => (typeof id === "number" ? (versions.get(id) ?? 0) : 0));
  return version ? `/api/users/${id}/avatar?v=${version}` : null;
}

/** The same outside React (the rave's dancers), from what's been loaded so far. */
export function avatarUrl(id: number): string | null {
  const version = versions.get(id);
  return version ? `/api/users/${id}/avatar?v=${version}` : null;
}
